/**
 * Floor-layout validation.
 *
 * A layout is only useful to the 3D twin if a person can actually walk it: the
 * process view drops an agent on a floor and expects it to reach any desk, to
 * reach the stairs or lift when the next task is on another storey, and to get
 * into the building in the first place. These rules encode exactly that, so a
 * layout that would strand the agent is caught here rather than discovered
 * later in the 3D view.
 *
 * Pure functions, no React and no network, so the rules can be exercised
 * headlessly (see scripts/verify-floor-layout.mjs).
 */

export const CELL_ROLES = [
  "EMPTY",
  "CORRIDOR",
  "LOBBY",
  "ENTRANCE",
  "ELEVATOR",
  "STAIRS",
  "VOID",
];

/**
 * Cells a person walks THROUGH. An entrance is walkable: it is the doorway you
 * arrive at and step through, so circulation must be able to flow from it.
 */
export const WALKABLE = new Set(["CORRIDOR", "LOBBY", "ENTRANCE"]);

/**
 * Vertical circulation. These are BUILDING-level: they are placed once on the
 * building's own cell layout, and every floor created afterwards is stamped
 * from it, which is what makes a core line up from storey to storey. They are
 * therefore locked on a floor plan — the designer will not paint one, and will
 * not paint over one. What a floor plan IS responsible for is putting a
 * corridor beside each core so people can walk into it.
 */
export const SHAFTS = new Set(["ELEVATOR", "STAIRS"]);

/** The cell roles a floor plan may actually paint. */
export const PAINTABLE_ROLES = ["EMPTY", "CORRIDOR", "LOBBY", "ENTRANCE", "VOID"];
export const isLockedRole = (cellType) => SHAFTS.has(cellType);

export const cellKey = (row, column) => `${row},${column}`;

const NEIGHBOURS = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

function readCell(floor, row, column) {
  const cells = floor.cells || {};
  const raw = cells instanceof Map ? cells.get(cellKey(row, column)) : cells[cellKey(row, column)];
  return raw || { cellType: "EMPTY", rowSpan: 1, colSpan: 1, absorbedBy: null };
}

function inBounds(floor, row, column) {
  return row >= 1 && row <= floor.rows && column >= 1 && column <= floor.columns;
}

// ---------------------------------------------------------------------------
// Multi-cell geometry
// ---------------------------------------------------------------------------

const spanOf = (cell) => ({
  rowSpan: Math.max(1, cell.rowSpan || 1),
  colSpan: Math.max(1, cell.colSpan || 1),
});

/** True when this cell has been swallowed by a larger room. */
export const isAbsorbed = (cell) => cell?.absorbedBy != null;

/**
 * Every [row, column] a room occupies. A single cell returns just itself; an
 * absorbed cell belongs to its owner and returns nothing of its own.
 */
export function footprint(floor, row, column) {
  const cell = readCell(floor, row, column);
  if (isAbsorbed(cell)) return [];
  const { rowSpan, colSpan } = spanOf(cell);
  const cells = [];
  for (let r = row; r < row + rowSpan; r++) {
    for (let c = column; c < column + colSpan; c++) {
      if (inBounds(floor, r, c)) cells.push([r, c]);
    }
  }
  return cells;
}

/** The rooms on a floor — anchors and single cells, never absorbed cells. */
export function roomsOf(floor) {
  const rooms = [];
  for (let row = 1; row <= floor.rows; row++) {
    for (let column = 1; column <= floor.columns; column++) {
      const cell = readCell(floor, row, column);
      if (isAbsorbed(cell)) continue;
      rooms.push({ row, column, cell, cells: footprint(floor, row, column) });
    }
  }
  return rooms;
}

/**
 * Does a ROOM touch circulation anywhere along its perimeter? A big room only
 * needs one way in, so any cell of its footprint touching a walkable cell is
 * enough — that is the door.
 */
function roomTouchesCirculation(floor, room) {
  const own = new Set(room.cells.map(([r, c]) => cellKey(r, c)));
  return room.cells.some(([r, c]) =>
    NEIGHBOURS.some(([dr, dc]) => {
      const nr = r + dr;
      const nc = c + dc;
      if (!inBounds(floor, nr, nc)) return false;
      if (own.has(cellKey(nr, nc))) return false; // inside itself, not a door
      return WALKABLE.has(readCell(floor, nr, nc).cellType);
    }),
  );
}

/** Is any cell of this room on the building's outer edge? */
function roomOnPerimeter(floor, room) {
  return room.cells.some(
    ([r, c]) => r === 1 || c === 1 || r === floor.rows || c === floor.columns,
  );
}

/**
 * Group walkable cells into connected runs. More than one run means the floor
 * is split: a person on one run cannot reach the other without walking through
 * rooms, which the twin does not allow.
 */
export function circulationRuns(floor) {
  const seen = new Set();
  const runs = [];
  for (let row = 1; row <= floor.rows; row++) {
    for (let column = 1; column <= floor.columns; column++) {
      const key = cellKey(row, column);
      if (seen.has(key)) continue;
      if (!WALKABLE.has(readCell(floor, row, column).cellType)) continue;

      const run = [];
      const stack = [[row, column]];
      while (stack.length) {
        const [r, c] = stack.pop();
        const k = cellKey(r, c);
        if (seen.has(k)) continue;
        seen.add(k);
        run.push([r, c]);
        NEIGHBOURS.forEach(([dr, dc]) => {
          const nr = r + dr;
          const nc = c + dc;
          if (!inBounds(floor, nr, nc)) return;
          if (WALKABLE.has(readCell(floor, nr, nc).cellType) && !seen.has(cellKey(nr, nc))) {
            stack.push([nr, nc]);
          }
        });
      }
      runs.push(run);
    }
  }
  return runs;
}

/** A census of one floor, used by both the checks and the stat tiles. */
export function summariseFloor(floor) {
  const rooms = [];
  const circulation = [];
  const entrances = [];
  const shafts = [];
  const voids = [];
  const absorbed = [];
  const unnamedSpaces = [];
  const mergedRooms = [];

  for (let row = 1; row <= floor.rows; row++) {
    for (let column = 1; column <= floor.columns; column++) {
      const cell = readCell(floor, row, column);
      const at = [row, column];
      if (isAbsorbed(cell)) {
        absorbed.push(at);
        continue;
      }
      const { rowSpan, colSpan } = spanOf(cell);
      if (rowSpan > 1 || colSpan > 1) mergedRooms.push(at);

      if (cell.cellType === "VOID") voids.push(at);
      else if (cell.cellType === "ENTRANCE") {
        entrances.push(at);
        circulation.push(at);
      } else if (WALKABLE.has(cell.cellType)) circulation.push(at);
      else if (SHAFTS.has(cell.cellType)) shafts.push(at);
      else {
        rooms.push(at);
        if (!cell.spaceTypeId) unnamedSpaces.push(at);
      }
    }
  }
  return { rooms, circulation, entrances, shafts, voids, absorbed, mergedRooms, unnamedSpaces };
}

const issue = (severity, code, title, detail, cells = []) => ({
  severity,
  code,
  title,
  detail,
  cells,
});

/**
 * Validate a single floor.
 *
 * `floors` (all floors of the building) is used to know whether this is the
 * lowest storey and whether the building has more than one. `buildingCells` is
 * the building's own cell layout — the authority on where the cores are.
 */
export function validateFloor(floor, floors = null, buildingCells = null) {
  const issues = [];
  const census = summariseFloor(floor);
  const rooms = roomsOf(floor);

  const occupied = rooms.filter(
    (r) => r.cell.cellType !== "VOID" && !WALKABLE.has(r.cell.cellType),
  );

  // --- 1. can every space be entered? ------------------------------------
  if (occupied.length && census.circulation.length === 0) {
    issues.push(
      issue(
        "error",
        "no-circulation",
        "This floor has no corridor or lobby",
        "Nothing on the floor can be reached. Paint a corridor so people can move between the spaces.",
        occupied.flatMap((r) => r.cells),
      ),
    );
  } else {
    const stranded = occupied.filter((r) => !roomTouchesCirculation(floor, r));
    const strandedRooms = stranded.filter((r) => !SHAFTS.has(r.cell.cellType));
    const strandedShafts = stranded.filter((r) => SHAFTS.has(r.cell.cellType));

    if (strandedRooms.length) {
      issues.push(
        issue(
          "error",
          "unreachable-rooms",
          `${strandedRooms.length} room${strandedRooms.length === 1 ? "" : "s"} cannot be entered`,
          "Each one needs a corridor or lobby along one of its edges. They are outlined on the plan.",
          strandedRooms.flatMap((r) => r.cells),
        ),
      );
    }
    if (strandedShafts.length) {
      issues.push(
        issue(
          "error",
          "unreachable-shafts",
          `${strandedShafts.length} stair or lift core cannot be entered`,
          "A core is placed on the building and cannot be moved from here — put a corridor next to it instead, or the agent cannot change storey.",
          strandedShafts.flatMap((r) => r.cells),
        ),
      );
    }
  }

  // --- 2. is the circulation one continuous run? --------------------------
  const runs = circulationRuns(floor);
  if (runs.length > 1) {
    issues.push(
      issue(
        "error",
        "split-circulation",
        `Circulation is split into ${runs.length} separate runs`,
        "Join them up. As drawn, someone standing on one run cannot reach the other.",
        runs
          .slice()
          .sort((a, b) => b.length - a.length)
          .slice(1)
          .flat(),
      ),
    );
  }

  // --- 3. the way IN ------------------------------------------------------
  // The entrance is what tells the twin which way the building faces and where
  // a visitor arrives, so the lowest storey must have one and upper storeys
  // must not. Which storey is lowest is only knowable with the building's other
  // floors in hand, so a caller that has not loaded them gets no verdict here
  // rather than a guess.
  const knowsStorey = Array.isArray(floors) && floors.length > 0;
  const isLowestFloor = knowsStorey && floor.id === lowestFloorOf(floors).id;

  if (!knowsStorey) {
    // nothing to say
  } else if (isLowestFloor) {
    if (census.entrances.length === 0) {
      issues.push(
        issue(
          "error",
          "no-entrance",
          "This floor has no entrance",
          "The lowest floor needs a way in. Paint an entrance on a cell at the edge of the building — it also sets which way the building faces.",
        ),
      );
    } else {
      const offEdge = census.entrances.filter(([r, c]) => {
        const room = rooms.find((x) => x.row === r && x.column === c);
        return room && !roomOnPerimeter(floor, room);
      });
      if (offEdge.length) {
        issues.push(
          issue(
            "error",
            "entrance-not-on-edge",
            `${offEdge.length} entrance${offEdge.length === 1 ? " is" : "s are"} not on the outside of the building`,
            "An entrance has to sit on the building's outer edge — nobody can walk in through the middle of a floor plate.",
            offEdge,
          ),
        );
      }
    }
  } else if (census.entrances.length) {
    issues.push(
      issue(
        "error",
        "entrance-above-ground",
        `${census.entrances.length} entrance${census.entrances.length === 1 ? "" : "s"} on an upper floor`,
        "Only the lowest floor has a way in from outside. Use a lobby here instead.",
        census.entrances,
      ),
    );
  }

  // --- 4. vertical circulation --------------------------------------------
  const multiStorey = Array.isArray(floors) && floors.length > 1;
  const declared = Array.isArray(buildingCells) ? buildingCells : null;

  if (declared) {
    if (multiStorey && declared.filter((c) => SHAFTS.has(c.type)).length === 0) {
      issues.push(
        issue(
          "error",
          "no-shaft",
          "This building has no lift or stairs",
          "It has more than one storey, so people need a way between them. Cores are placed on the building itself — add one in Edit Building.",
        ),
      );
    }

    const drifted = [];
    declared
      .filter((c) => SHAFTS.has(c.type))
      .forEach((c) => {
        if (!inBounds(floor, c.row, c.column)) return;
        if (readCell(floor, c.row, c.column).cellType !== c.type) {
          drifted.push([c.row, c.column]);
        }
      });
    if (drifted.length) {
      issues.push(
        issue(
          "error",
          "core-missing",
          `${drifted.length} core${drifted.length === 1 ? "" : "s"} the building defines ${drifted.length === 1 ? "is" : "are"} missing from this floor`,
          "The building's layout puts a lift or stairs here, but this floor does not have one. Re-save the building layout so the floors match, or the agent cannot change storey here.",
          drifted,
        ),
      );
    }

    const declaredKeys = new Set(
      declared.filter((c) => SHAFTS.has(c.type)).map((c) => cellKey(c.row, c.column)),
    );
    const unexpected = census.shafts.filter(([r, c]) => !declaredKeys.has(cellKey(r, c)));
    if (unexpected.length) {
      issues.push(
        issue(
          "error",
          "core-unexpected",
          `${unexpected.length} core${unexpected.length === 1 ? "" : "s"} on this floor ${unexpected.length === 1 ? "is" : "are"} not part of the building`,
          "A lift or stairs only works if it is in the same cell on every storey. This one is not on the building's layout, so it leads nowhere.",
          unexpected,
        ),
      );
    }
  } else if (multiStorey && census.shafts.length === 0) {
    issues.push(
      issue(
        "error",
        "no-shaft",
        "No stairs or lift on this floor",
        "The building has more than one storey, so the agent needs a way on and off this one.",
      ),
    );
  }

  // --- 5. merge integrity --------------------------------------------------
  // The designer maintains these, but a plan can arrive from the API too.
  const owner = new Map();
  const overlaps = [];
  const outOfGrid = [];
  rooms.forEach((room) => {
    const { rowSpan, colSpan } = spanOf(room.cell);
    if (room.row + rowSpan - 1 > floor.rows || room.column + colSpan - 1 > floor.columns) {
      outOfGrid.push([room.row, room.column]);
      return;
    }
    room.cells.forEach(([r, c]) => {
      const k = cellKey(r, c);
      if (owner.has(k)) overlaps.push([r, c]);
      owner.set(k, room);
    });
  });
  if (outOfGrid.length) {
    issues.push(
      issue(
        "error",
        "merge-out-of-grid",
        `${outOfGrid.length} merged room${outOfGrid.length === 1 ? "" : "s"} extend${outOfGrid.length === 1 ? "s" : ""} past the grid`,
        "Shrink the room so it fits inside the building's rows and columns.",
        outOfGrid,
      ),
    );
  }
  if (overlaps.length) {
    issues.push(
      issue(
        "error",
        "merge-overlap",
        `${overlaps.length} cell${overlaps.length === 1 ? " is" : "s are"} claimed by two rooms`,
        "Split one of the overlapping rooms apart.",
        overlaps,
      ),
    );
  }

  // --- 6. completeness (worth fixing, does not block) ---------------------
  if (census.unnamedSpaces.length) {
    issues.push(
      issue(
        "warning",
        "no-space-type",
        `${census.unnamedSpaces.length} room${census.unnamedSpaces.length === 1 ? " has" : "s have"} no purpose set`,
        "Choose what each space is used for so the twin can furnish and label it.",
        census.unnamedSpaces,
      ),
    );
  }

  return { issues, census, runs, rooms };
}

/** The lowest storey: explicit level where present, else the code convention. */
export function lowestFloorOf(floors) {
  return [...floors].sort((a, b) => {
    if (a.level != null && b.level != null) return a.level - b.level;
    if (a.level != null) return -1;
    if (b.level != null) return 1;
    return String(a.code ?? "").localeCompare(String(b.code ?? ""));
  })[0];
}

/** True when nothing would strand the agent. Warnings do not block. */
export const canSave = (issues) => !issues.some((i) => i.severity === "error");
